import { Alert, Button, Table } from '@odin-labs/components';
import {
  SitePermission,
  useGetSiteDevicesQuery,
} from 'apollo/generated/client-operations';
import { NewHeader } from 'components/header/NewHeader';
import { PlusIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import React from 'react';
import { ReaderKindBadge, ReaderHealth } from 'siteLayout/readerPresentation';
import { useSitePermissions } from 'sitePermissions/useSitePermissions';
import { AddSiteControllerDrawer } from './AddSiteControllerDrawer';
import { getColumns, SiteControllerColumn } from './DevicesTab.tables';
import { placementText } from './SiteControllerDrawer.tables';
import { SiteControllerDrawer } from './SiteControllerDrawer';

/** The API's largest page of controllers; a site with more is not expected. */
const PAGE = 100;

const classes = {
  container: 'odin-flex odin-flex-col odin-gap-y-6 odin-p-6',
  subtitle: 'odin--mt-4 odin-text-sm odin-text-gray-500',
  empty:
    'odin-rounded-lg odin-border odin-border-gray-200 odin-bg-white odin-p-10 odin-text-center odin-text-sm odin-text-gray-500',
};

/**
 * The controllers assigned to this site with their readers. Adding,
 * syncing and removing are offered only to a user the API's permission
 * catalog allows to manage the site's devices; the API checks the same
 * catalog on every command.
 */
export function DevicesTab({
  jobsiteId: siteId,
  onTabApiChange,
  onIsDirtyChange,
}: JobsiteConfigTabProps): React.ReactElement {
  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = React.useState<
    string | null
  >(null);
  const { data, loading, error, refetch } = useGetSiteDevicesQuery({
    variables: { siteId, first: PAGE },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const reload = React.useCallback(
    async () => (await refetch()).data.siteDevices,
    [refetch],
  );
  const permissions = useSitePermissions(siteId);
  const canManage =
    !permissions.loading && permissions.can(SitePermission.ManageSiteDevices);
  const controllers = React.useMemo(() => data?.siteDevices ?? [], [data]);
  const selected =
    controllers.find(
      ({ assignmentId }) => assignmentId === selectedAssignmentId,
    ) ?? null;
  const columns = React.useMemo<SiteControllerColumn[]>(() => getColumns(), []);

  React.useEffect(
    () =>
      onTabApiChange?.({
        refetchData: refetch,
        loading,
        jobsiteName: data?.getJobsite?.name ?? null,
      }),
    [onTabApiChange, refetch, loading, data?.getJobsite?.name],
  );
  React.useEffect(() => onIsDirtyChange(false), [onIsDirtyChange]);

  // A read that failed before anything was shown is the tab's whole
  // state; one that failed on a reload keeps the last inventory on screen.
  if (error && !data) return <LoadingError error={error.message} />;

  return (
    <>
      <div className={classes.container}>
        {error && (
          <Alert
            type="danger"
            text="Could not reload the site's devices; what is shown may be out of date."
          />
        )}
        <NewHeader
          title="Devices"
          titleInfo={controllers.length}
          size="md"
          actionsProps={{
            headerActions: null,
            children: canManage ? (
              <Button
                icon={PlusIcon}
                text="Add controller"
                size="sm"
                onClick={() => setIsAddOpen(true)}
              />
            ) : null,
          }}
        />
        {controllers.length === PAGE && (
          <Alert
            type="info"
            text={`Showing the first ${PAGE} controllers, as many as one page holds.`}
          />
        )}
        <p className={classes.subtitle}>
          Controllers assigned to this site, with the readers that belong to
          them. Open a controller to see its ports and readers
          {canManage ? ', sync it from Avigilon, or remove it' : ''}.
        </p>
        {!loading && controllers.length === 0 ? (
          <p className={classes.empty}>No controllers are on this site.</p>
        ) : (
          <Table
            loading={loading && controllers.length === 0}
            columns={columns}
            data={controllers}
            onRowClick={({ data: row }) => {
              setSelectedAssignmentId(row.assignmentId);
              setIsDetailsOpen(true);
            }}
            remote
            disableGlobalFilter
            disableSortBy
          />
        )}
        {(data?.siteIndependentReaders?.length ?? 0) > 0 && (
          <section className="odin-space-y-3">
            <h3 className="odin-text-base odin-font-medium">
              Independent readers
            </h3>
            <p className="odin-text-sm odin-text-gray-500">
              Readers registered on this site independently of a controller.
            </p>
            <ul className="odin-space-y-2">
              {data.siteIndependentReaders.map((reader) => (
                <li
                  key={reader.readerId}
                  className="odin-flex odin-items-center odin-gap-2 odin-text-sm"
                >
                  <span>
                    {reader.name}
                    {reader.serialNumber ? ` (${reader.serialNumber})` : ''}
                  </span>
                  <ReaderKindBadge kind={reader.kind} />
                  <ReaderHealth reader={reader} />
                  <span>{placementText(reader.placement)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <AddSiteControllerDrawer
        isOpen={isAddOpen}
        siteId={siteId}
        onClose={() => setIsAddOpen(false)}
        onChanged={reload}
        knownSerialNumbers={controllers.map(({ serialNumber }) => serialNumber)}
      />
      <SiteControllerDrawer
        open={isDetailsOpen}
        siteId={siteId}
        controller={selected}
        canManage={canManage}
        onClose={() => setIsDetailsOpen(false)}
        onAfterLeave={() => {
          setIsDetailsOpen(false);
          setSelectedAssignmentId(null);
        }}
        onChanged={reload}
      />
    </>
  );
}
